// Lightweight structured client logger + error capture.
//
// Goals: give the frontend the same "legible behavior" property as the backend
// (one structured line per event) without pulling a heavy SDK into every bundle.
// Errors are also forwarded to an optional global sink so a real error tracker
// can be wired in later by registering the sink once, with no changes to call
// sites.

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogFields {
  [key: string]: unknown;
}

export type ErrorSink = (error: unknown, context?: LogFields) => void;

interface SinkHost {
  __vadiumErrorSink__?: ErrorSink;
}

function sinkHost(): SinkHost {
  return (typeof window !== "undefined" ? (window as unknown as SinkHost) : (globalThis as unknown as SinkHost)) ?? {};
}

/** Register an error sink (e.g. a Cloud Error Reporting client). Returns an unregister fn. */
export function registerErrorSink(sink: ErrorSink): () => void {
  sinkHost().__vadiumErrorSink__ = sink;
  return () => {
    delete sinkHost().__vadiumErrorSink__;
  };
}

function emit(level: LogLevel, message: string, fields?: LogFields): void {
  const entry = { level, message, ts: new Date().toISOString(), ...fields };
  const line = JSON.stringify(entry);
  if (level === "error") console.error(line);
  else if (level === "warn") console.warn(line);
  else console.log(line);
}

export const logger = {
  debug: (message: string, fields?: LogFields) => emit("debug", message, fields),
  info: (message: string, fields?: LogFields) => emit("info", message, fields),
  warn: (message: string, fields?: LogFields) => emit("warn", message, fields),
  error: (message: string, fields?: LogFields) => emit("error", message, fields)
};

/** Log an error and forward it to the registered error sink, if any. */
export function captureError(error: unknown, context?: LogFields): void {
  const message = error instanceof Error ? error.message : String(error);
  emit("error", message, { ...context, error_type: error instanceof Error ? error.name : typeof error });
  const sink = sinkHost().__vadiumErrorSink__;
  if (sink) {
    try {
      sink(error, context);
    } catch {
      // Never let observability throw into application code.
    }
  }
}
